import React, { useState } from 'react';
import { Badge, IconButton, Popover, List, Typography, Box, Tooltip, Divider } from '@mui/material';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/pro-solid-svg-icons';
import { preview } from '@europrocurement/l2d-icons';
import { Link } from 'react-router-dom';
import { CustomAvatar, CustomAvatarProps } from '../CustomAvatar';
import { FlexyIconButton } from '../../atoms';

export type IconBadgeListItem = {
    id: string | number;
    title: string;
    message: string;
    avatar?: CustomAvatarProps | undefined;
    link?: string;
    date?: string;
    redirect?: string;
    count?: number;
    remove?: () => void;
};

export type tooltipBadge = 'Notifications' | 'Messages';

export type IconBadgeListProps = {
    icon?: FontAwesomeIconProps['icon'];
    iconSize?: FontAwesomeIconProps['size'];
    color?: 'default' | 'primary' | 'secondary' | 'error';
    count?: number;
    tooltip?: tooltipBadge;
    items: IconBadgeListItem[] | null;
    width?: number;
};

export const IconBadgeList: React.FunctionComponent<IconBadgeListProps> = function ({
    icon = faBell,
    iconSize = 'lg',
    color = 'primary',
    count,
    tooltip = 'Notifications',
    items,
    width = 360,
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const stripHtml = (str: string) => str.replace(/<[^>]*>?/gm, '');
    return (
        <>
            <Tooltip title={tooltip}>
                <IconButton
                    color="inherit"
                    onClick={handleOpen}
                >
                    <Badge
                        badgeContent={count && count > 0 ? count : 0}
                        color={color}
                        invisible={!count || count <= 0}
                        max={10}
                    >
                        <FontAwesomeIcon
                            icon={icon}
                            size={iconSize}
                        />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: { width, maxHeight: 420, borderRadius: 2, overflow: 'auto' },
                    },
                }}
            >
                <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 600 }}
                    >
                        {tooltip}
                    </Typography>
                </Box>

                <List
                    dense
                    disablePadding
                >
                    {items !== null && items.length === 0 && (
                        <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">Aucun élément</Typography>
                        </Box>
                    )}

                    {items !== null &&
                        items.map((item, i) => (
                            <React.Fragment key={item.id}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    flexDirection="row"
                                >
                                    <Link
                                        to={item?.redirect ?? ''}
                                        style={{
                                            textDecoration: 'none',
                                            padding: '15px',
                                            width: '100%',
                                            color: 'text.secondary',
                                        }}
                                        onClick={() => item.remove?.()}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            flexDirection="row"
                                            gap="8px"
                                        >
                                            <CustomAvatar
                                                firstName={item.avatar?.firstName || 'Anonyme'}
                                                lastName={item.avatar?.lastName || 'Anonyme'}
                                                src={item.avatar?.src}
                                                keycloakId={item.avatar?.keycloakId}
                                                sx={{ height: '32px', width: '32px' }}
                                            />
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                width="100%"
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography
                                                            variant="l2dbodys"
                                                            color="text.primary"
                                                        >
                                                            {item?.title}
                                                        </Typography>
                                                    </Box>
                                                    {item.date && (
                                                        <Box>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {item.date}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Box>
                                                        {item.link ? (
                                                            <Link
                                                                to={item.link}
                                                                target="_blank"
                                                                color="primary"
                                                            >
                                                                <Typography
                                                                    variant="l2dbodyxs"
                                                                    color="text.primary"
                                                                >
                                                                    {stripHtml(item.message)
                                                                        .length > 100
                                                                        ? `${stripHtml(item.message).slice(0, 100)}…`
                                                                        : stripHtml(item.message)}
                                                                </Typography>
                                                            </Link>
                                                        ) : (
                                                            <Typography
                                                                variant="l2dbodyxs"
                                                                color="text.primary"
                                                            >
                                                                {stripHtml(item.message).length >
                                                                100
                                                                    ? `${stripHtml(item.message).slice(0, 100)}…`
                                                                    : stripHtml(item.message)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {item?.count && (
                                                        <Box>
                                                            <Badge
                                                                badgeContent={
                                                                    item.count && item.count > 0
                                                                        ? item.count
                                                                        : 0
                                                                }
                                                                color={color}
                                                                invisible={
                                                                    !item.count || item.count <= 1
                                                                }
                                                                max={10}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Link>
                                    {item.remove !== undefined && (
                                        <FlexyIconButton
                                            icon={preview}
                                            onClick={item.remove}
                                        />
                                    )}
                                </Box>
                                {i < items.length - 1 && (
                                    <Divider
                                        variant="inset"
                                        component="li"
                                    />
                                )}
                            </React.Fragment>
                        ))}
                </List>
            </Popover>
        </>
    );
};
